import { useEffect, useState } from 'react';
import './RPCDemo.css';

// Scripted fixtures illustrate a comparison; no providers are contacted.
const requests = [
  { method: 'eth_chainId', value: '0x1' },
  { method: 'eth_blockNumber', value: '0x12d687' },
  { method: 'eth_getBalance', value: '0x1bc16d674ec80000' },
  { method: 'eth_getTransactionCount', value: '0x2a' },
  { method: 'eth_getCode', value: '0x' },
  { method: 'eth_getBlockByNumber', value: 'number: 0x12d687' },
  { method: 'eth_getLogs', value: '[]' },
  { method: 'eth_getTransactionReceipt', value: 'gasUsed: 0x5208', candidate: 'gasUsed: 0x7530' },
];

function Terminal({ candidate, visible }) {
  const start = Math.max(0, visible - 5);
  const reservedRequests = requests.slice(-5);
  return <div className="rpc-terminal">
    <div className="rpc-terminal-heading"><div className="rpc-terminal-title"><span className="rpc-window-dots" aria-hidden="true"><i /><i /><i /></span><span className="rpc-provider-label">Provider {candidate ? 'B' : 'A'}</span></div></div>
    <div className="rpc-terminal-body">
      <ol start={requests.length - 4} className="rpc-log rpc-log-reserve" aria-hidden="true">
        {reservedRequests.map((request, index) => <li key={`reserve-${index}`} className={request.candidate ? 'rpc-log-mismatch' : ''}>
          <span className="rpc-log-number">{String(requests.length - 4 + index).padStart(2, '0')}</span><div><span className="rpc-log-method">{request.method}</span><span className="rpc-log-value"><span aria-hidden="true">↳ </span>{candidate && request.candidate ? request.candidate : request.value}</span></div><span className="rpc-log-mark" />
        </li>)}
      </ol>
      {visible === 0 ? <p className="rpc-terminal-idle"><span aria-hidden="true">$ </span>Waiting for requests<span className="rpc-cursor" aria-hidden="true">_</span></p> : <ol start={start + 1} className="rpc-log rpc-log-active">
        {requests.slice(start, visible).map((request, index) => <li key={start + index} className={request.candidate ? 'rpc-log-mismatch' : ''}>
          <span className="rpc-log-number">{String(start + index + 1).padStart(2, '0')}</span>
          <div><span className="rpc-log-method">{request.method}</span><span className="rpc-log-value"><span aria-hidden="true">↳ </span>{candidate && request.candidate ? request.candidate : request.value}</span></div>
          <span className="rpc-log-mark" aria-label={request.candidate ? 'Mismatch' : 'Match'}>{request.candidate ? '≠' : '✓'}</span>
        </li>)}
      </ol>}
    </div>
  </div>;
}

export default function RPCDemo() {
  const [visible, setVisible] = useState(0);
  const [running, setRunning] = useState(false);
  const [started, setStarted] = useState(false);
  const failed = visible === requests.length;
  const controlLabel = running ? 'Pause' : failed ? 'Replay' : started ? 'Resume' : 'Run';

  useEffect(() => {
    if (!running) return undefined;
    const timer = setTimeout(() => {
      const next = visible + 1;
      setVisible(next);
      if (next === requests.length) setRunning(false);
    }, 700);
    return () => clearTimeout(timer);
  }, [visible, running]);

  const run = () => {
    if (running) {
      setRunning(false);
      return;
    }
    setStarted(true);
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      setVisible(requests.length);
      return;
    }
    if (failed) setVisible(0);
    setRunning(true);
  };

  return <div className="rpc-demo" aria-label="RPCDiff scripted demo">
    <div className="rpc-demo-toolbar"><span className="muted">Two providers. Same requests.</span><button type="button" onClick={run}>{controlLabel}<svg className="rpc-playback-icon" viewBox="0 0 20 20" aria-hidden="true" focusable="false">
      {running ? <><rect x="5" y="4" width="3" height="12" /><rect x="12" y="4" width="3" height="12" /></> : failed ? <path d="M16 8a6 6 0 1 0 0 5M16 3v5h-5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /> : <path d="M6 3.5 16 10 6 16.5Z" />}
    </svg></button></div>
    <div className="rpc-terminals"><Terminal visible={visible} /><Terminal candidate visible={visible} /></div>
    <div className={`rpc-demo-result${failed ? ' has-mismatch' : ''}`} role="status" aria-live="polite" aria-atomic="true">
    <p className="rpc-demo-caption">*Scripted illustration</p>

    </div>
  </div>;
}
